export type AnalysisCapabilityReadiness =
  | "current_data_ready"
  | "backfill_bound"
  | "forward_accumulation"
  | "new_source_required";

export interface AnalysisDataCapability {
  id: string;
  label: string;
  readiness: AnalysisCapabilityReadiness;
  existingSourceIds: string[];
  missingFields: string[];
  implementedNow: boolean;
  note: string;
}

/**
 * Capability matrix is deliberately about data availability, not feature marketing.
 * `backfill_bound` means the current registered source can provide the needed history
 * and the collector simply has not completed it yet. `forward_accumulation` means
 * we can preserve future vintages from the current source but time alone cannot
 * reconstruct vintages that were never archived. `new_source_required` must never
 * be silently substituted with an adjacent series.
 */
export function buildAnalysisDataCapabilityMatrix(): AnalysisDataCapability[] {
  return [
    {
      id: "price-volume-state",
      label: "가격 × 거래량 시장상태",
      readiness: "current_data_ready",
      existingSourceIds: ["bok-ecos-housing-price", "molit-rtms-housing-transactions"],
      missingFields: [],
      implementedNow: true,
      note: "공식 주택가격지수와 RTMS 완결월 거래량을 결합합니다. RTMS backfill이 늘수록 과거 상태전이 표본이 자동 확장됩니다."
    },
    {
      id: "price-volume-band-state",
      label: "가격대별 가격 × 거래량 divergence",
      readiness: "backfill_bound",
      existingSourceIds: ["molit-rtms-housing-transactions"],
      missingFields: [],
      implementedNow: true,
      note: "현재 RTMS만으로 계산 가능하며 법정동코드×법정동×단지명×전용면적 5㎡ origin-cohort 공통셀을 인접기간에 매칭합니다. 15/30/50억 시장 band crossing은 탈락시키지 않고 migration으로 보존하며 composition gap·breadth·region-mix·cutoff/quality sensitivity를 함께 제공합니다. 동일 개별호 repeat-sales는 아니고 안정적 장기 구조분석은 RTMS MAX backfill 완결에 의존합니다."
    },
    {
      id: "rate-asymmetry",
      label: "금리 인상/인하 비대칭",
      readiness: "current_data_ready",
      existingSourceIds: ["bok-ecos-core", "bok-ecos-housing-price", "molit-rtms-housing-transactions"],
      missingFields: [],
      implementedNow: true,
      note: "현재 ECOS 금리와 공식 가격지수로 즉시 계산하고, RTMS 거래량 경로는 backfill이 늘수록 안정성이 개선됩니다."
    },
    {
      id: "rate-level-interaction",
      label: "금리 인상/인하 × 상대 고금리/저금리 상태의존성",
      readiness: "current_data_ready",
      existingSourceIds: ["bok-ecos-core", "bok-ecos-housing-price"],
      missingFields: [],
      implementedNow: true,
      note: "각 월 이전 최대 60개월만으로 상대 금리수준을 분류해 full-sample threshold look-ahead를 피합니다. high/low 양쪽에 같은 방향 shock 월이 각각 8개 미만이면 interaction LP를 계산하지 않습니다. 절대 고금리/저금리 구간이나 causal regime effect를 뜻하지 않습니다."
    },
    {
      id: "rate-threshold-kink",
      label: "절대 금리 threshold/kink 탐색",
      readiness: "current_data_ready",
      existingSourceIds: ["bok-ecos-core", "bok-ecos-housing-price"],
      missingFields: [],
      implementedNow: true,
      note: "0.25%p 절대 주담대금리 cutoff 후보를 탐색하고 양쪽 최소 24개월·각 20% 표본을 요구합니다. 겹치는 6개월 가격반응의 자기상관과 여러 cutoff 선택편향을 block-permutation max-statistic으로 방어하며, 보정 후 경계를 넘지 못하면 no robust threshold로 남깁니다. full-sample 탐색이므로 PIT-safe 또는 causal threshold가 아닙니다."
    },
    {
      id: "market-state-change-point",
      label: "가격×거래 시장상태 retrospective change-point",
      readiness: "backfill_bound",
      existingSourceIds: ["bok-ecos-housing-price", "molit-rtms-housing-transactions"],
      missingFields: [],
      implementedNow: true,
      note: "연속 완결구간에서 가능한 모든 break month의 전·후 가격·거래 평균과 상태구성 분포 차이를 CUSUM형 통계로 탐색합니다. 양쪽 최소 12개월·전체 연속구간 최소 36개월을 요구하고 3개월 moving-block permutation의 전 후보월 최대통계량 95% 경계를 넘을 때만 후보로 승격합니다. 단순 RTMS 가격 proxy는 composition bias 때문에 제외하며 full post-break sample을 사용하므로 실시간 PIT trigger가 아닌 retrospective 분석입니다."
    },
    {
      id: "spatial-seoul-gyeonggi",
      label: "서울 ↔ 경기 spatial lead-lag",
      readiness: "backfill_bound",
      existingSourceIds: ["molit-rtms-housing-transactions", "bok-ecos-core"],
      missingFields: [],
      implementedNow: true,
      note: "서울·경기 RTMS 동일정의 거래량을 양방향 1~12M으로 비교하고 lag block-permutation 및 공통 주담대금리 변화 민감도 조정을 적용합니다. fixed-cell 가격 경로는 파생값이 존재할 때만 활성화됩니다. 현재 MAX backfill이 불연속/부분이면 insufficient 또는 limited가 정상이며 causal spatial spillover로 해석하지 않습니다."
    },
    {
      id: "policy-rate-interaction",
      label: "정책 제약강도 × 금리 shock interaction",
      readiness: "forward_accumulation",
      existingSourceIds: ["fsc-housing-policy-regimes", "official-policy-auto-watcher", "bok-ecos-core"],
      missingFields: ["과거 전체기간의 exact LTV/DSR/절대한도 수치 레짐"],
      implementedNow: true,
      note: "확정 숫자 레짐만 사용합니다. 현재 등록된 exact 정책기간이 짧으면 insufficient가 정상입니다. 자동수집 정책 후보를 임의 숫자로 바꾸지 않습니다."
    },
    {
      id: "hysteresis",
      label: "같은 금리수준에서 인상기/인하기 반응 차이",
      readiness: "current_data_ready",
      existingSourceIds: ["bok-ecos-core", "bok-ecos-housing-price"],
      missingFields: [],
      implementedNow: true,
      note: "동일 금리구간의 상승기/하락기 이후 공식 가격 반응을 비교하며 permutation null을 통과해야 후보로 표시합니다."
    },
    {
      id: "pit-forward-vintage",
      label: "앞으로의 PIT snapshot/vintage 축적",
      readiness: "forward_accumulation",
      existingSourceIds: ["bok-ecos-core", "bok-ecos-housing-price", "molit-rtms-housing-transactions"],
      missingFields: [],
      implementedNow: false,
      note: "과거 ECOS PIT/vintage evidence는 중앙 이관본으로 보존되지만 FREM-local snapshot workflow와 provider credential 경로는 retired입니다. 신규 forward-vintage 자동 축적은 SDP-side revision/vintage collector가 별도로 검증되기 전까지 미구현으로 유지하며, 과거 미보존 vintage를 자동 복원한다고 가정하지 않습니다."
    },
    {
      id: "listing-inventory",
      label: "기존주택 매물량/매물회수",
      readiness: "new_source_required",
      existingSourceIds: [],
      missingFields: ["매물 재고", "신규등록", "매물철회", "DOM 또는 동등한 유동성 지표"],
      implementedNow: false,
      note: "현재 ECOS/RTMS/정책 API에는 기존주택 listing inventory가 없습니다. 신규 소스 없이는 Price × Volume × Inventory로 승격하지 않습니다."
    },
    {
      id: "buyer-financing",
      label: "현금매수/실제 LTV·DSR/차주구성",
      readiness: "new_source_required",
      existingSourceIds: [],
      missingFields: ["거래별 대출사용액", "실제 LTV", "실제 DSR", "현금매수 여부"],
      implementedNow: false,
      note: "공개 RTMS 매매 API의 거래가격만으로 자금조달방식을 추정하지 않습니다."
    },
    {
      id: "forced-sale",
      label: "연체 → 경매/강제매도 → 가격",
      readiness: "new_source_required",
      existingSourceIds: [],
      missingFields: ["주담대 연체", "주택 경매 신규건수", "낙찰률", "낙찰가율"],
      implementedNow: false,
      note: "현재 시계열만으로 강제매도 경로를 직접 관측할 수 없습니다."
    },
    {
      id: "true-policy-surprise",
      label: "금통위 기대 대비 외생 surprise",
      readiness: "new_source_required",
      existingSourceIds: ["bok-ecos-core"],
      missingFields: ["결정 직전 시장기대 또는 고빈도 surprise series"],
      implementedNow: false,
      note: "기준금리 변경일 KTB1Y 재평가는 event proxy일 뿐입니다. 시간 경과만으로 true surprise로 승격하지 않습니다."
    },
    {
      id: "historical-exact-pit",
      label: "과거 exact release-vintage replay",
      readiness: "new_source_required",
      existingSourceIds: [],
      missingFields: ["과거 발표시점별 원본 vintage archive"],
      implementedNow: false,
      note: "현재 최신 revision history를 오래 보관해도 과거에 실제 공개됐던 수치를 복원할 수는 없습니다. 공식 과거 발표본 archive가 필요합니다."
    }
  ];
}
